import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Box,
  Button,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const ScheduleKVsScheduleK1 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Schedule K vs Schedule K-1: Key Differences for S Corporations"
          description="Compare IRS Schedule K vs Schedule K-1 for S corporations. Learn how income, deductions, and credits flow from the business to each shareholder."
          keywords=""
          canonical="resources/business-tax/schedule-k-vs-schedule-k-1/"
          ogType="article"
          jsonLdPricing="179.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="1658"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Schedule K vs Schedule K-1</span>
          </span>
          <br />
          What&apos;s the Difference for S Corporations?
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  component="article"
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/business-tax/schedule-k-vs-schedule-k-1/"
                  />
                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-25T09:00:00-05:00"
                      >
                        Aug 25, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-25T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 25, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href="https://www.taxzerone.com/"
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    component="h2"
                    itemProp="headline"
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      mb: 0.8,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Schedule K vs. Schedule K-1 (Form 1120-S) at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="1"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Understand the difference between Schedule K and
                          Schedule K-1,
                        </strong>{' '}
                        including the purpose of each schedule and how they
                        support Form 1120-S reporting.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="2"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Learn how Schedule K and Schedule K-1 work together
                        </strong>{' '}
                        to report S corporation income, deductions, credits, and
                        each shareholder's allocated share.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="3"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Avoid common reporting errors
                        </strong>{' '}
                        by understanding IRS matching requirements, who prepares
                        each schedule, and why both must be accurate and
                        consistent.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  S corporations don&apos;t pay income tax at the company level.
                  Instead, profits, losses, and tax attributes flow directly to
                  shareholders.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Two IRS schedules make this pass-through system work:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Schedule K</span> and{' '}
                  <span style={{ fontWeight: 600 }}>Schedule K-1</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  They sound similar, but they serve very different roles. One
                  summarizes the corporation&apos;s tax activity. The other
                  assigns those numbers to each owner.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Understanding how these two schedules work together is
                  critical for filing Form 1120-S correctly, keeping
                  shareholders compliant, and avoiding IRS mismatches.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  This guide explains how Schedule K and Schedule K-1 differ,
                  how they connect, and why both are required.
                </Typography>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#high" sx={{ color: '#0202ff' }}>
                          High-Level Difference Between Schedule K and Schedule
                          K-1
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schk" sx={{ color: '#0202ff' }}>
                          What Schedule K Does
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schk1" sx={{ color: '#0202ff' }}>
                          What Schedule K-1 Does
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How Schedule K and Schedule K-1 Work Together
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#why" sx={{ color: '#0202ff' }}>
                          Why the IRS Requires Both
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Common Problems When These Schedules Are Mismatched
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#which" sx={{ color: '#0202ff' }}>
                          Which One Do You Need to Prepare?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#where" sx={{ color: '#0202ff' }}>
                          Where These Schedules Fit in the Filing Process
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#handles" sx={{ color: '#0202ff' }}>
                          How TaxZerone Handles Schedule K and K-1
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#final" sx={{ color: '#0202ff' }}>
                          Final Summary
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="high"
                >
                  High-Level Difference Between Schedule K and Schedule K-1
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              width: 200,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Feature
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 200,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Schedule K
                          </TableCell>
                          <TableCell sx={{ width: 200, fontWeight: 600 }}>
                            Schedule K-1
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Who it belongs to
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            The S corporation
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Each individual shareholder
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f1f1f1' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            What it shows
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Total income, deductions, and credits for the
                            business
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Each owner’s share of those totals
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            How many exist
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            One per tax return
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            One for every shareholder
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f1f1f1' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Who files it
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            The S corporation
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Filed by the S corporation and given to shareholders
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            What it supports
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Corporate-level reporting
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Personal tax reporting by Shareholders
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="schk"
                >
                  What Schedule K Does
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Schedule K works at the{' '}
                  <span style={{ fontWeight: 600 }}>business level</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  It collects everything the S corporation earned, spent,
                  deducted, or credited during the year and presents it in a
                  single structured summary.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This includes:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Business profits or losses</li>
                  <li>Rental and investment income</li>
                  <li>Capital gains</li>
                  <li>Special deductions</li>
                  <li>Tax credits</li>
                  <li>Tax-exempt or nondeductible items</li>
                  <li>Distributions to shareholders</li>
                  <li>Other separately stated items required by IRS rules</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS uses Schedule K to verify that the corporation&apos;s
                  financial activity is fully disclosed and properly categorized
                  before anything is passed to shareholders.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Think of Schedule K as the{' '}
                  <span style={{ fontWeight: 600 }}>master ledger</span> of the
                  S corporation&apos;s tax results.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="schk1"
                >
                  What Schedule K-1 Does
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Schedule K-1 works at the{' '}
                  <span style={{ fontWeight: 600 }}>owner level</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Once Schedule K establishes the totals, Schedule K-1 divides
                  those totals among the shareholders based on ownership
                  percentage.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Each K-1 tells one shareholder:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>How much income they must report</li>
                  <li>What losses they can deduct</li>
                  <li>Which credits apply to them</li>
                  <li>What special tax items affect their return</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  These numbers flow directly into the shareholder&apos;s
                  personal tax return — typically Schedule E on Form 1040.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  No K-1 means the shareholder cannot report their S corporation
                  income correctly.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="how"
                >
                  How Schedule K and Schedule K-1 Work Together
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  These schedules are mathematically and legally connected.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS expects that:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  The total of all Schedule K-1s must equal Schedule K.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For example:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    If Schedule K shows $200,000 of business income and there
                    are two 50% owners
                  </li>
                  <li>Each K-1 must report $100,000</li>
                  <li>
                    If the numbers don&apos;t match, the IRS flags the return —
                    even if the dollar amounts are correct.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  This is one of the most common causes of IRS notices for S
                  corporations.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="why"
                >
                  Why the IRS Requires Both
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Schedule K and K-1 exist for different enforcement reasons:
                </Typography>
                <Box component="div" sx={{ mb: 1 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              width: '60%',
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            IRS Goal
                          </TableCell>
                          <TableCell sx={{ width: '40%', fontWeight: 600 }}>
                            Which Schedule Supports It
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Verify the corporation reported all income
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Schedule K
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f1f1f1' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Confirm shareholders reported their share
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Schedule K-1
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Match corporate and personal returns
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>Both</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 12 }}>
                    Without Schedule K, the IRS can&apos;t validate the company.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Without Schedule K-1, the IRS can&apos;t validate the
                    owners.{' '}
                  </li>
                </ul>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="common"
                >
                  Common Problems When These Schedules Are Mismatched
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  When Schedule K and Schedule K-1 don&apos;t align, it causes:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>IRS notices and audits</li>
                  <li style={{ marginBottom: 12 }}>
                    Delays in refund processing
                  </li>
                  <li style={{ marginBottom: 12 }}>Amended returns</li>
                  <li style={{ marginBottom: 12 }}>Shareholder tax errors</li>
                  <li style={{ marginBottom: 12 }}>
                    Penalties for the corporation
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  These mistakes usually come from:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 12 }}>IRS notices and audits</li>
                  <li style={{ marginBottom: 12 }}>
                    Delays in refund processing
                  </li>
                  <li style={{ marginBottom: 12 }}>Amended returns</li>
                  <li style={{ marginBottom: 12 }}>Shareholder tax errors</li>
                  <li style={{ marginBottom: 12 }}>
                    Penalties for the corporation
                  </li>
                </ul>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="which"
                >
                  Which One Do You Need to Prepare?
                </Typography>
                <Box component="div" sx={{ mb: 1 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              width: '30%',
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Role
                          </TableCell>
                          <TableCell sx={{ width: '80%', fontWeight: 600 }}>
                            What You Need
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            S corporation
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Schedule K + one Schedule K-1 per shareholder
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f1f1f1' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Shareholder
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Only your Schedule K-1
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            IRS
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>Both</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Shareholders never file Schedule K.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Corporations never give Schedule K to shareholders.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  They are different tools for different users — but they must
                  match perfectly.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="where"
                >
                  Where These Schedules Fit in the Filing Process
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>S corporation completes Form 1120-S</li>
                  <li>Schedule K summarizes all tax activity</li>
                  <li>Schedule K-1 divides it among owners</li>
                  <li>IRS receives all schedules together</li>
                  <li>Shareholders use K-1 to file their own returns</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  If any piece is missing, the entire return is incomplete.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="handles"
                >
                  How TaxZerone Handles Schedule K and K-1
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  When you file Form 1120-S through{' '}
                  <span style={{ fontWeight: 600 }}>TaxZerone</span>:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    You enter Schedule K details using guided step-by-step
                    fields
                  </li>
                  <li>
                    Schedule K totals are calculated and validated automatically
                  </li>
                  <li>
                    Schedule K-1s are generated for each shareholder based on
                    your entries
                  </li>
                  <li>Ownership percentages are checked for accuracy</li>
                  <li>Totals are reconciled before submission</li>
                  <li>IRS-ready schedules are attached in one filing</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  This eliminates manual errors and prevents mismatches that
                  cause IRS notices.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="final"
                >
                  Final Summary
                </Typography>
                <Box component="div" sx={{ mb: 1 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              width: '30%',
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Schedule
                          </TableCell>
                          <TableCell sx={{ width: '80%', fontWeight: 600 }}>
                            Purpose
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Schedule K
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Reports the S corporation’s total tax activity
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f1f1f1' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Schedule K-1
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            Tells each shareholder what portion belongs to them
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    You can&apos;t file one without the other - and you
                    can&apos;t file either incorrectly.
                  </li>
                  <li>
                    Understanding the difference is the key to correct S
                    corporation tax reporting.
                  </li>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to e-file Form 1120-S with accurate Schedule K and K-1?
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Simplify S corporation compliance and avoid IRS mismatches.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=schkvsschk1`}
              >
                Start e-Filing Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default ScheduleKVsScheduleK1;
